"use client";

/**
 * The behaviour every blocking overlay needs, and that hand-rolled ones skip.
 *
 * The first version of the overlays prototype handled Escape and a click
 * outside, and nothing else — which meant Tab walked straight out of the modal
 * into the page behind it, a screen reader never heard it announced as a
 * dialog, and the body scrolled under the scrim. Those are correctness bugs,
 * not polish, and they are the reason the shipped `Modal` wraps Radix rather
 * than being drawn by hand.
 *
 * It is hand-rolled rather than Radix on purpose: `Sheet` needs the trap, the
 * scroll lock and the Escape contract without Radix's own positioning, and
 * the cost of the treatment is visible here rather than hidden in a library.
 */

import { type RefObject, useEffect, useRef } from "react";

const FOCUSABLE = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

export function useOverlay({
  open,
  onClose,
  ref,
}: {
  open: boolean;
  onClose: () => void;
  ref: RefObject<HTMLElement | null>;
}) {
  /** Where focus was before the overlay opened, so it can go back there. */
  const restoreTo = useRef<HTMLElement | null>(null);

  /**
   * The callback is almost always written inline at the call site, so it is a
   * new function on every parent render. Depending on it directly makes the
   * effect a lifecycle: ticking a checkbox inside the panel re-renders the
   * parent, the effect tears down — restoring focus to the opener — and sets
   * up again on the panel. Focus is pulled out from under the reader on every
   * keystroke. Holding it in a ref keeps the effect tied to `open` alone.
   */
  const latestClose = useRef(onClose);
  latestClose.current = onClose;

  useEffect(() => {
    if (!open) return;
    restoreTo.current = document.activeElement as HTMLElement | null;

    // Scroll lock. Padding replaces the scrollbar's width so the page behind
    // does not jump sideways as it disappears — the shift is small and reads
    // as a glitch rather than as the overlay opening.
    const { body } = document;
    const gutter = window.innerWidth - document.documentElement.clientWidth;
    const prevOverflow = body.style.overflow;
    const prevPad = body.style.paddingRight;
    body.style.overflow = "hidden";
    if (gutter > 0) body.style.paddingRight = `${gutter}px`;

    // Focus the panel itself rather than its first control: a dialog that
    // opens with the destructive button focused invites a reflex Enter.
    const panel = ref.current;
    panel?.focus({ preventScroll: true });

    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        // Escape belongs to the innermost thing that is open. This listener is
        // on `document` in the capture phase, so it runs before any React
        // handler inside the panel — a popover calling `stopPropagation` in a
        // bubble handler has already lost by the time it is asked. So the
        // popover announces itself in the DOM instead, and this defers.
        //
        // Scoped to this overlay's own subtree, not the document: these pages
        // deliberately mount a flat wizard and a modal one at the same time,
        // so a claim made inside one of them must not swallow Escape in the
        // other. Querying the document was that bug waiting to happen.
        if (panel?.querySelector("[data-escape-capture='true']")) return;
        event.stopPropagation();
        latestClose.current();
        return;
      }
      if (event.key !== "Tab" || !panel) return;

      // The trap. Queried on each Tab rather than cached, because the panel's
      // contents can change while it is open.
      const items = Array.from(
        panel.querySelectorAll<HTMLElement>(FOCUSABLE),
      ).filter((el) => el.offsetParent !== null);
      if (items.length === 0) {
        event.preventDefault();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;

      if (event.shiftKey && (active === first || active === panel)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("keydown", onKey, true);
      body.style.overflow = prevOverflow;
      body.style.paddingRight = prevPad;
      // Return focus to what opened it. Without this the reader is dropped at
      // the top of the document with no idea where they were.
      restoreTo.current?.focus({ preventScroll: true });
    };
    // `onClose` is deliberately absent: it lives in a ref above.
  }, [open, ref]);
}

export function useDismiss({
  open,
  onClose,
  ref,
}: {
  open: boolean;
  onClose: () => void;
  ref: RefObject<HTMLElement | null>;
}) {
  const latestClose = useRef(onClose);
  latestClose.current = onClose;

  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node))
        latestClose.current();
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") latestClose.current();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, ref]);
}
